import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import {
  DpiaOverallRisk,
  mapDpiaOverallRiskToBadgeClass,
} from '../../../../constants/assessment.constants';

/** Tonal-pill palette for a DpiaOverallRisk value.
 *
 *   LOW    → success (green)
 *   MEDIUM → warning (amber)
 *   HIGH   → error   (red)
 *
 * Deliberately narrower than `mapDpiaOverallRiskToBadgeClass`' return
 * type (which also produces `'info'` for the null case) — this
 * component doesn't render when `risk` is absent, so the `'info'`
 * branch never reaches the template. */
type DpiaRiskBadgeTone = 'success' | 'warning' | 'error';

/**
 * Canonical DPIA risk-band chip.
 *
 * Renders a single tonal pill with the localised label for a
 * `DpiaOverallRisk` value (`Low` / `Medium` / `High`). Consolidates
 * four previously-duplicated per-surface pill CSS blocks
 * (`.report__risk-badge`, `.dpia-risk-card__risk-badge`,
 * `.dpia-full-profile__pill`, and their `--success/warning/error`
 * modifiers) into one atom of the design system, so every DPIA
 * surface — drawer report, edit-route Report tab, edit-route Profile
 * tab — reads the same when the same risk band is displayed.
 *
 * Deliberately layout-agnostic:
 *   - No "Residual: " prefix, no leading arrow. Callers arrange
 *     Overall → Residual pairs with an external arrow icon so the
 *     component stays a pure atom that composes cleanly into any
 *     surface (chip row, snapshot cell, matrix legend, …).
 *   - Renders NOTHING when `risk` is null / undefined. Callers decide
 *     the empty-state affordance (an em-dash placeholder, hiding the
 *     row, etc.). This matches the drawer-report snapshot's
 *     "— placeholder" behaviour that predates the extraction.
 *
 * Enum → tone mapping funnels through
 * `mapDpiaOverallRiskToBadgeClass` so a new `DpiaOverallRisk` band
 * (should one ever be added) is picked up in one place.
 */
@Component({
  selector: 'app-dpia-risk-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule],
  templateUrl: './risk-badge.html',
  styleUrl: './risk-badge.css',
})
export class DpiaRiskBadgeComponent {
  readonly risk = input<DpiaOverallRisk | null | undefined>(undefined);

  /** Tone class suffix — drives the `--success / --warning / --error`
   *  visual variant. Null when no risk is set (template gates render). */
  protected readonly tone = computed<DpiaRiskBadgeTone | null>(() => {
    const r = this.risk();
    if (!r) return null;
    const mapped = mapDpiaOverallRiskToBadgeClass(r);
    // Safe narrowing: LOW/MEDIUM/HIGH exhaust `DpiaOverallRisk` and
    // each maps to one of the three tonal variants. The `'info'` case
    // in the util's return type only fires for null input, which is
    // handled by the guard above.
    return mapped === 'success' || mapped === 'warning' || mapped === 'error' ? mapped : null;
  });

  /** i18n key for the localised band label — same key namespace
   *  (`dpia.matrix.overall_*`) that every DPIA surface uses today, so
   *  swapping the inline `t('dpia.matrix.overall_' + …)` templates for
   *  this component doesn't touch translations. */
  protected readonly labelKey = computed<string | null>(() => {
    const r = this.risk();
    return r ? `dpia.matrix.overall_${r.toLowerCase()}` : null;
  });
}
